import { faTrash, faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../core/components/Button.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import Chip from '../config/Chip.tsx';

const RemoveIcon = withIcon(faTrash);
const SyncIcon = withIcon(faCloudArrowDown);

interface AgentKnowledgeFile {
    id: string;
    fileName: string;
    fileUrl: string;
    createdAt: string;
    updatedAt: string;
    isSynced: boolean;
    sourceType: string | null;
    sourceItemId: string | null;
    sourceSyncedAt: string | null;
}

const SOURCE_LABEL_KEYS: Record<string, string> = {
    url: 'form.sourceUrl',
    sharepoint: 'form.sourceSharepoint',
    sftp: 'form.sourceSftp',
};

const sourceLabelKey = (sourceType: string) =>
    sourceType.startsWith('master_data') ? 'form.sourceMasterData' : SOURCE_LABEL_KEYS[sourceType];

interface FileItemProps {
    name: string;
    url?: string;
    isSynced?: boolean;
    sourceType?: string | null;
    sourceSyncedAt?: string | null;
    details?: React.ReactNode;
    canDelete?: boolean;
    onDelete: () => void;
}

const FileItem = ({
    name,
    url,
    isSynced,
    sourceType,
    sourceSyncedAt,
    details,
    canDelete = true,
    onDelete,
}: FileItemProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const labelKey = sourceType ? sourceLabelKey(sourceType) : null;

    return (
        <div className="w-full flex justify-between items-center border border-solid border-line-weak rounded p-3 shadow-sm text-content-strong">
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {url ? (
                        <a href={url} className="text-sm font-medium" target="_blank" rel="noopener noreferrer">
                            {name}
                        </a>
                    ) : (
                        <span className="text-sm font-medium">{name}</span>
                    )}
                    {isSynced && labelKey && <Chip>{t(labelKey)}</Chip>}
                </div>
                {isSynced && (
                    <div className="flex items-center gap-1.5 text-xs text-content-medium">
                        <SyncIcon />
                        <span>{t('form.synced')}</span>
                        {sourceSyncedAt && (
                            <span className="text-content-weak">
                                &middot;{' '}
                                {t('form.lastSynced', {
                                    time: formatDistanceToNow(new Date(sourceSyncedAt), { addSuffix: true }),
                                })}
                            </span>
                        )}
                    </div>
                )}
                {!isSynced && details && <div className="text-xs text-content-medium">{details}</div>}
            </div>
            {!isSynced && canDelete && (
                <Button
                    variant="ghost"
                    onClick={onDelete}
                    className="text-content-danger-strong"
                    type="button"
                    aria-label={t('form.removeFile')}
                >
                    <RemoveIcon />
                </Button>
            )}
        </div>
    );
};

interface Props {
    files: (File | AgentKnowledgeFile)[];
    onDelete: (file: File | AgentKnowledgeFile) => void;
    // optional per-file hooks for holders with per-user rules (shared projects); defaults keep the agent behaviour
    canDelete?: (file: File | AgentKnowledgeFile) => boolean;
    renderDetails?: (file: AgentKnowledgeFile) => React.ReactNode;
}

const KnowledgeFileList = ({ files, onDelete, canDelete, renderDetails }: Props) => {
    const isExistingFile = (file: File | AgentKnowledgeFile): file is AgentKnowledgeFile => {
        return 'id' in file && 'fileUrl' in file;
    };

    return (
        <div className="flex flex-col gap-2">
            {files.map((file, index) => {
                const isExisting = isExistingFile(file);
                const name = isExisting ? file.fileName : file.name;
                const url = isExisting ? file.fileUrl : undefined;
                const key = isExisting ? file.id : `new-${index}`;

                return (
                    <FileItem
                        key={key}
                        name={name}
                        url={url}
                        isSynced={isExisting ? file.isSynced : undefined}
                        sourceType={isExisting ? file.sourceType : undefined}
                        sourceSyncedAt={isExisting ? file.sourceSyncedAt : undefined}
                        details={isExisting && renderDetails ? renderDetails(file) : undefined}
                        canDelete={canDelete ? canDelete(file) : true}
                        onDelete={() => onDelete(file)}
                    />
                );
            })}
        </div>
    );
};

export default KnowledgeFileList;
